import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { generatePassword } from "@passwordflow/crypto";
import type { GenCharset } from "@passwordflow/crypto";
import { ArrowRight, Copy, RefreshCw, SlidersHorizontal, Wand2 } from "lucide-react";
import { useSecretCopy } from "../../app/clipboard.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { paths } from "../../app/paths.js";
import { estimateStrength } from "../../lib/strength.js";
import { PasswordGlyphs, StrengthMeter, Switch } from "../../ui/forms.js";
import { Button, Card, CardHeader, Page, PageHeader } from "../../ui/primitives.js";

const SETS: ReadonlyArray<{ readonly key: GenCharset; readonly label: string; readonly sample: string }> = [
  { key: "lower", label: "Kleinbuchstaben", sample: "a–z" },
  { key: "upper", label: "Großbuchstaben", sample: "A–Z" },
  { key: "digits", label: "Ziffern", sample: "2–9" },
  { key: "symbols", label: "Symbole", sample: "!@#$%…" }
];

const MIN_LENGTH = 12;
const MAX_LENGTH = 64;

function generate(length: number, sets: ReadonlyArray<GenCharset>): string {
  return generatePassword({ length, sets: [...sets] });
}

export function GeneratorPage(): ReactNode {
  useDocumentTitle("Passwort-Generator");
  const navigate = useNavigate();
  const secretCopy = useSecretCopy();
  const [length, setLength] = useState(24);
  const [sets, setSets] = useState<GenCharset[]>(["lower", "upper", "digits", "symbols"]);
  const [password, setPassword] = useState(() => generate(24, ["lower", "upper", "digits", "symbols"]));

  function regenerate(nextLength: number = length, nextSets: ReadonlyArray<GenCharset> = sets): void {
    setPassword(generate(nextLength, nextSets));
  }

  function toggle(key: GenCharset, on: boolean): void {
    const next = on ? [...sets, key] : sets.filter((set: GenCharset): boolean => set !== key);
    if (next.length === 0) {
      return;
    }
    setSets(next);
    regenerate(length, next);
  }

  async function copy(): Promise<void> {
    await secretCopy(password, "Passwort kopiert");
  }

  const report = estimateStrength(password);

  return (
    <Page>
      <PageHeader
        eyebrow="Werkzeuge"
        title="Passwort-Generator"
        description={
          <>
            Zufall aus dem Browser (<span className="font-mono text-[0.8125rem]">crypto.getRandomValues</span>). Nichts verlässt dein Gerät.
          </>
        }
      />

      <div className="mt-7 grid items-start gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card className="flex flex-col">
          <CardHeader title="Ergebnis" description={`${report.label} · ca. ${report.bits} Bit Entropie`} icon={Wand2} />
          <div className="flex flex-col gap-5 p-5">
            <div className="rounded-lg border border-line-strong bg-ink px-5 py-6">
              <p aria-live="polite" data-testid="generated-password">
                <PasswordGlyphs value={password} className="text-[1.3rem] tracking-[0.02em] sm:text-[1.45rem]" />
              </p>
            </div>
            <StrengthMeter password={password} />
            <div className="flex flex-wrap gap-2">
              <Button icon={Copy} onClick={(): void => void copy()}>
                Kopieren
              </Button>
              <Button variant="secondary" icon={RefreshCw} onClick={(): void => regenerate()}>
                Neu würfeln
              </Button>
              <Button variant="ghost" onClick={(): void => void navigate(paths.vaultNew, { state: { password } })}>
                Als neuen Eintrag verwenden
                <ArrowRight size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>
        </Card>

        <Card className="flex flex-col">
          <CardHeader title="Optionen" description="Mindestens ein Zeichensatz bleibt aktiv." icon={SlidersHorizontal} />
          <div className="flex flex-col gap-6 p-5">
            <div>
              <div className="flex items-baseline justify-between">
                <label htmlFor="gen-length" className="text-sm font-medium text-foam">
                  Länge
                </label>
                <span className="rounded-md border border-line bg-ink px-2 py-0.5 font-mono text-sm font-semibold tabular-nums text-flow">{length}</span>
              </div>
              <input
                id="gen-length"
                type="range"
                min={MIN_LENGTH}
                max={MAX_LENGTH}
                value={length}
                onChange={(event): void => {
                  const next = Number(event.target.value);
                  setLength(next);
                  regenerate(next, sets);
                }}
                className="mt-3 w-full accent-[#62F0D8]"
              />
              <div className="mt-1.5 flex justify-between text-xs text-mist-2">
                <span>{MIN_LENGTH}</span>
                <span>{MAX_LENGTH}</span>
              </div>
            </div>
            <div className="divide-y divide-line rounded-lg border border-line bg-ink">
              {SETS.map((set) => (
                <div key={set.key} className="flex items-center gap-4 px-4 py-2">
                  <div className="min-w-0 flex-1">
                    <Switch
                      label={set.label}
                      description={set.sample}
                      checked={sets.includes(set.key)}
                      disabled={sets.length === 1 && sets.includes(set.key)}
                      onChange={(on: boolean): void => toggle(set.key, on)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
